import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
  Checkbox, FormControlLabel,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";

import { fetchDataItemsByDataType, updateDataItem, fetchDataDomains } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function DataItemEditPage() {
  const { businessDomainId, dataTypeId, dataItemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [original, setOriginal] = useState(location.state || null);
  const [form, setForm] = useState(null);
  const [dataDomains, setDataDomains] = useState([]);

  const backPath = `/design/${businessDomainId}/data-types/${dataTypeId}/data-items/${dataItemId}`;

  const initForm = (d) => ({
    dataItemName: d.dataItemName || "",
    dataItemDescription: d.dataItemDescription || "",
    url: d.url || "",
    primaryKey: d.primaryKey || false,
    foreignKey: d.foreignKey || false,
    referenceAttributeId: d.referenceAttributeId || "",
    dataDomainId: d.dataDomainId || "",
    dataDomainName: d.dataDomainName || "",
  });

  useEffect(() => {
    // Pre-fill from location state immediately
    if (location.state && !form) setForm(initForm(location.state));

    (async () => {
      try {
        const domains = await fetchDataDomains(COMPANY_ID);
        setDataDomains(Array.isArray(domains) ? domains : []);
      } catch (e) { /* ignore */ }
      try {
        const items = await fetchDataItemsByDataType(dataTypeId);
        const found = (Array.isArray(items) ? items : []).find((i) => i.dataItemId === dataItemId);
        if (found) { setOriginal(found); setForm(initForm(found)); }
      } catch (e) { notify.apiError(e); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.dataItemName) { notify.error(t("dataItem.name.required")); return; }
    const domain = dataDomains.find((d) => d.dataDomainId === form.dataDomainId);
    try {
      await updateDataItem({
        ...original,
        ...form,
        dataDomainName: domain?.dataDomainName || form.dataDomainName || "",
      });
      notify.success(t("message.update.success"));
      navigate(backPath);
    } catch (err) { notify.apiError(err); }
  };

  if (!form) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("dataItem.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth required label={t("dataItem.name")}
              name="dataItemName" value={form.dataItemName} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3} label={t("dataItem.description")}
              name="dataItemDescription" value={form.dataItemDescription} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("dataItem.dataDomain")}</InputLabel>
              <Select name="dataDomainId" value={form.dataDomainId} label={t("dataItem.dataDomain")} onChange={handleChange}>
                <MenuItem value=""><em>—</em></MenuItem>
                {dataDomains.map((d) => (
                  <MenuItem key={d.dataDomainId} value={d.dataDomainId}>{d.dataDomainName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={<Checkbox name="primaryKey" checked={form.primaryKey} onChange={handleChange} />}
              label={t("dataItem.primaryKey")} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={<Checkbox name="foreignKey" checked={form.foreignKey} onChange={handleChange} />}
              label={t("dataItem.foreignKey")} />
          </Grid>
          {form.foreignKey && (
            <Grid size={12}>
              <TextField fullWidth label={t("dataItem.referenceAttributeId")}
                name="referenceAttributeId" value={form.referenceAttributeId} onChange={handleChange} />
            </Grid>
          )}
          <Grid size={12}>
            <TextField fullWidth type="url" label={t("common.url")}
              name="url" value={form.url} onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(backPath)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
